"use client";

import React, { useCallback, useState } from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Button, Checkbox, Form, Input } from "antd";
import Text from "antd/es/typography/Text";
import * as Sentry from "@sentry/nextjs";

const SMS_CONSENT_ENABLED =
  process.env.NEXT_PUBLIC_SMS_CONSENT_ENABLED !== "false";

/** Shown when a customer gate check returns block=true. */
export const CUSTOMER_BLOCKED_MESSAGE =
  "You are unable to book with this business at this time. Please contact us for assistance.";

import AppointmentModalReview from "./components/AppointmentReview";
import { PhoneInput } from "@openings/ui";
import lookupStyles from "./LookupPhoneNumber.module.css";
import styles from "./appointmentFlow.module.css";
import InlineError from "./components/InlineError";

import { ComponentProps } from "./types";
import {
  FIRST_TIME_CLIENT,
  MODAL_COMPONENT_SIZE,
  RESCHEDULE_MAIN,
  SETUP_PAYMENT,
} from "./consts";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import type { AppointmentHistory } from "@openings/api-client";
import { useLocale } from "@/lib/i18n";
import {
  shouldApplyCustomerDepositGate,
  shouldOfferExistingAppointmentReschedule,
} from "./quoteRouting";

const LookupPhoneNumber: React.FC<ComponentProps> = ({
  appointmentDetails,
  state,
  setState,
  onCreateAppointment,
}) => {
  const { t } = useLocale();
  const verificationMethod = appointmentDetails.verificationMethod ?? "SMS";
  const isEmail = verificationMethod === "EMAIL";
  const smsConsentNeeded = SMS_CONSENT_ENABLED && !isEmail;
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [isSubmitting, setSubmitting] = useState(false);
  // Consent starts unticked on every booking and is never remembered: the
  // device may be the lobby kiosk or a shared computer, and consent has to
  // come from the person booking (#772).
  const [hasSmsConsent, setSmsConsent] = useState(!smsConsentNeeded);
  const [inputError, setInputError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const submitError = useCallback((err: Error) => {
    setErrorMessage(err.message ?? "An unknown error occurred.");
    setSubmitting(false);
  }, []);

  const fetchHistory = useCallback(async (): Promise<AppointmentHistory> => {
    if (!appointmentDetails?.salonId) {
      throw new Error("Missing salonId.");
    }

    if (isEmail) {
      if (!email) throw new Error("Email is required.");
      const res = await bookingFlowApi.appointmentHistoryByEmail({
        email,
        salonId: appointmentDetails.salonId,
      });
      if (res.ok) return res.data;
      throw new Error("Failed to fetch appointment history");
    } else {
      if (!appointmentDetails?.userId) throw new Error("Missing userId.");
      if (!phoneNumber) throw new Error("Phone number is required.");
      const res = await bookingFlowApi.appointmentHistory({
        phoneNumber,
        userId: appointmentDetails.userId,
        salonId: appointmentDetails.salonId,
      });
      if (res.ok) return res.data;
      throw new Error("Failed to fetch appointment history");
    }
  }, [
    appointmentDetails?.userId,
    appointmentDetails?.salonId,
    phoneNumber,
    email,
    isEmail,
  ]);

  const handleRouting = useCallback(
    async (history: AppointmentHistory) => {
      const identifier = isEmail ? email : phoneNumber;
      if (!identifier) return;

      const {
        upcomingAppointments,
        needCreditCard,
        customerId,
        hasUpcomingAppointments,
        verificationPurpose,
      } = history;

      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "lookup:handleRouting",
        data: {
          salonId: appointmentDetails.salonId,
          isEmail,
          hasCustomerId: Boolean(customerId),
          hasUpcomingAppointments: Boolean(hasUpcomingAppointments),
          upcomingCount: upcomingAppointments?.length ?? 0,
          needCreditCard: Boolean(needCreditCard),
          creditCardRequired: state.state.creditCardRequired,
          verificationPurpose: verificationPurpose ?? null,
        },
      });

      const stateUpdate = isEmail
        ? { email: email!, phoneNumber: "" }
        : { phoneNumber: phoneNumber! };

      // New client → go to first-time client route (no gate check needed yet)
      if (!customerId) {
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "lookup:route:first-time-client",
          data: { salonId: appointmentDetails.salonId },
        });
        setState((prev) => ({
          route: FIRST_TIME_CLIENT,
          state: {
            ...prev.state,
            ...stateUpdate,
          },
        }));
        return;
      }

      // Returning customer — check gate before routing
      let isCustomerBlocked = false;
      let isDepositRequired = false;
      try {
        const gateRes = await bookingFlowApi.getBookingGate({
          customerId,
          salonId: appointmentDetails.salonId,
          ...(appointmentDetails.teamMemberId
            ? { teamMemberId: appointmentDetails.teamMemberId }
            : {}),
        });
        if (!gateRes.ok || typeof gateRes.data?.gate?.block !== "boolean" ||
            typeof gateRes.data?.gate?.depositRequired !== "boolean") {
          throw new Error("Customer gate unavailable");
        }
        isCustomerBlocked = gateRes.data.gate.block;
        isDepositRequired = gateRes.data.gate.depositRequired;
      } catch {
        // Unknown policy cannot authorize card storage or appointment creation.
        Sentry.addBreadcrumb({
          category: "booking",
          level: "warning",
          message: "lookup:gate-check-failed",
          data: { salonId: appointmentDetails.salonId },
        });
        setErrorMessage(t.bookingNetworkError);
        setSubmitting(false);
        return;
      }

      if (isCustomerBlocked) {
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "lookup:route:blocked",
          data: { salonId: appointmentDetails.salonId, customerId },
        });
        setErrorMessage(CUSTOMER_BLOCKED_MESSAGE);
        setSubmitting(false);
        return;
      }

      const customerDepositRequired = shouldApplyCustomerDepositGate(
        {
          requestId: appointmentDetails.requestId,
          quotePublicId: appointmentDetails.quotePublicId,
        },
        isDepositRequired,
      );
      const requiresPayment = Boolean(
        needCreditCard ||
          state.state.creditCardRequired ||
          customerDepositRequired,
      );

      // If card required → go to payment setup
      if (requiresPayment) {
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "lookup:route:setup-payment",
          data: {
            salonId: appointmentDetails.salonId,
            customerId,
            isDepositRequired,
            customerDepositRequired,
            needCreditCard: Boolean(needCreditCard),
          },
        });
        setState((prev) => ({
          route: SETUP_PAYMENT,
          state: {
            ...prev.state,
            ...stateUpdate,
            upcomingAppointments,
            customerId,
            customerDepositRequired,
          },
        }));
        return;
      }

      // If there are upcoming appointments → reschedule
      if (
        shouldOfferExistingAppointmentReschedule(
          {
            requestId: appointmentDetails.requestId,
            quotePublicId: appointmentDetails.quotePublicId,
          },
          hasUpcomingAppointments || upcomingAppointments.length > 0,
        )
      ) {
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "lookup:route:reschedule-main",
          data: {
            salonId: appointmentDetails.salonId,
            customerId,
            upcomingCount: upcomingAppointments.length,
          },
        });
        setState((prev) => ({
          route: RESCHEDULE_MAIN,
          state: {
            ...prev.state,
            upcomingAppointments,
            hasUpcomingAppointments:
              hasUpcomingAppointments || upcomingAppointments.length > 0,
            rescheduleVerificationPurpose: verificationPurpose,
            ...stateUpdate,
            customerId,
          },
        }));
        return;
      }

      // Returning client with history → book directly without re-verification
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "lookup:route:auto-book-returning",
        data: {
          salonId: appointmentDetails.salonId,
          customerId,
        },
      });
      setState((prev) => ({
        ...prev,
        state: {
          ...prev.state,
          ...stateUpdate,
          customerId,
        },
      }));
      onCreateAppointment({
        customerId,
        errorCallback: () => setSubmitting(false),
      });
    },
    [
      isEmail,
      email,
      phoneNumber,
      onCreateAppointment,
      setState,
      state.state.creditCardRequired,
      t.bookingNetworkError,
      appointmentDetails.salonId,
      appointmentDetails.teamMemberId,
      appointmentDetails.requestId,
      appointmentDetails.quotePublicId,
    ],
  );

  const handleSubmit = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: "booking",
      level: "info",
      message: "lookup:handleSubmit:start",
      data: {
        salonId: appointmentDetails.salonId,
        userId: appointmentDetails.userId,
        isEmail,
        hasPhone: Boolean(phoneNumber),
        hasEmail: Boolean(email),
        hasSmsConsent,
      },
    });
    if (isEmail) {
      if (!email || !email.includes("@")) {
        setInputError(true);
        return;
      }
    } else {
      if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        setInputError(true);
        return;
      }
      if (SMS_CONSENT_ENABLED && !hasSmsConsent) {
        setErrorMessage(t.smsConsentRequired);
        return;
      }
    }
    setInputError(false);
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const history = await fetchHistory();
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "lookup:fetchHistory:done",
        data: {
          salonId: appointmentDetails.salonId,
          hasCustomerId: Boolean(history?.customerId),
          upcomingCount: history?.upcomingAppointments?.length ?? 0,
        },
      });
      await handleRouting(history);
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      Sentry.captureMessage("booking:lookup-failed", {
        level: "warning",
        tags: {
          booking_step: "lookup",
          salonId: appointmentDetails.salonId,
        },
        extra: {
          errorMessage: error.message,
          isEmail,
        },
      });
      submitError(error);
    }
  }, [
    fetchHistory,
    handleRouting,
    hasSmsConsent,
    isEmail,
    email,
    phoneNumber,
    submitError,
    t.smsConsentRequired,
    appointmentDetails.salonId,
    appointmentDetails.userId,
  ]);

  const canSubmit = isEmail
    ? Boolean(email)
    : Boolean(phoneNumber) && (!smsConsentNeeded || hasSmsConsent);

  return (
    <div className={styles.stepContainer}>
      <AppointmentModalReview appointment={appointmentDetails} />

      <Form
        layout="vertical"
        onFinish={handleSubmit}
        className={styles.compactForm}
      >
        {isEmail ? (
          <Form.Item
            label={<Text strong>{t.email ?? "Email"}</Text>}
            htmlFor="email"
          >
            <Input
              id="email"
              autoComplete="email"
              size={MODAL_COMPONENT_SIZE}
              placeholder={t.emailPlaceholder ?? "you@example.com"}
              type="email"
              value={email}
              status={inputError ? "error" : ""}
              onChange={(e) => {
                setEmail(e.target.value);
                if (inputError) setInputError(false);
              }}
            />
          </Form.Item>
        ) : (
          <>
            <Form.Item name="phoneNumber">
              <PhoneInput
                size={MODAL_COMPONENT_SIZE}
                label={t.phoneNumber}
                showInternationalToggle
                status={inputError ? "error" : ""}
                value={phoneNumber}
                onChange={(v) => {
                  setPhoneNumber(v);
                  if (inputError) setInputError(false);
                }}
              />
            </Form.Item>
            <Text type="secondary" className={styles.otpHint}>
              {t.otpHint}
            </Text>
            {smsConsentNeeded && (
              // No `name`: a named Form.Item injects its own `checked`, so
              // the box shown would not be the consent that enables Submit.
              <Form.Item>
                <Checkbox
                  checked={hasSmsConsent}
                  onChange={(event) => setSmsConsent(event.target.checked)}
                  className={lookupStyles.smsConsent}
                >
                  {t.smsConsent}
                </Checkbox>
              </Form.Item>
            )}
          </>
        )}
      </Form>

      <InlineError message={errorMessage} />

      <div className={styles.actionRow}>
        <Button
          size={MODAL_COMPONENT_SIZE}
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          loading={isSubmitting}
          type="primary"
          block
        >
          {t.submit}
        </Button>
      </div>
    </div>
  );
};

export default LookupPhoneNumber;
